import React, { useState, useEffect } from 'react';
import { Comic } from '../types/Comic';
import './ComicForm.css';

interface ComicFormProps {
  comic?: Comic | null;
  onSubmit: (comic: Partial<Comic>) => Promise<void>;
  onCancel: () => void;
}

const ComicForm: React.FC<ComicFormProps> = ({ comic, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Partial<Comic>>({
    name: '',
    email: '',
    instagram_handle: '',
    alternate_emails: [],
    gender: 'male',
    friend: false,
    is_cellar_comic: false,
    can_host: false,
    do_not_book: false,
    spot_rate: 'standard',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newAlternateEmail, setNewAlternateEmail] = useState('');

  useEffect(() => {
    if (comic) {
      setFormData(comic);
    }
  }, [comic]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpotRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRate = e.target.value as Comic['spot_rate'];
    setFormData(prev => ({
      ...prev,
      spot_rate: newRate,
      custom_spot_rate: newRate !== 'custom' ? undefined : prev.custom_spot_rate,
    }));
  };

  const handleCustomRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      custom_spot_rate: val === '' ? undefined : parseInt(val, 10),
    }));
  };

  const addAlternateEmail = () => {
    const trimmedEmail = newAlternateEmail.trim().toLowerCase();

    // Validation
    if (!trimmedEmail) {
      return;
    }

    if (!trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
      setError('Invalid email format');
      return;
    }

    if (trimmedEmail === formData.email?.toLowerCase()) {
      setError('Alternate email cannot match primary email');
      return;
    }

    const currentAlternates = formData.alternate_emails || [];
    if (currentAlternates.includes(trimmedEmail)) {
      setError('Email already in alternate emails list');
      return;
    }

    setFormData(prev => ({
      ...prev,
      alternate_emails: [...(prev.alternate_emails || []), trimmedEmail]
    }));
    setNewAlternateEmail('');
    setError(null);
  };

  const removeAlternateEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      alternate_emails: (prev.alternate_emails || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name?.trim()) {
      setError('Name is required');
      return;
    }

    if (!comic && !formData.email?.trim()) {
      setError('Email is required');
      return;
    }

    if (!comic && formData.email && !formData.email.includes('@')) {
      setError('Invalid email format');
      return;
    }

    if (formData.spot_rate === 'custom' && !formData.custom_spot_rate) {
      setError('Custom rate amount is required');
      return;
    }

    try {
      setLoading(true);
      await onSubmit(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save comic');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="comic-form-overlay">
      <div className="comic-form-container">
        <div className="comic-form-header">
          <h3>{comic ? 'Edit Comic' : 'Add New Comic'}</h3>
          <button onClick={onCancel} className="close-button" disabled={loading}>
            ×
          </button>
        </div>

        {error && (
          <div className="form-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="comic-form">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email (Primary) *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading || !!comic}
              required={!comic}
              placeholder="comic@example.com"
            />
            {comic && (
              <small className="form-hint">Email cannot be changed</small>
            )}
          </div>

          <div className="form-group">
            <label>Alternate Emails</label>
            <div className="alternate-emails-list">
              {(formData.alternate_emails || []).map((email, index) => (
                <div key={index} className="alternate-email-item">
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => removeAlternateEmail(index)}
                    className="remove-email-button"
                    disabled={loading}
                    title="Remove email"
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="add-alternate-email">
                <input
                  type="email"
                  value={newAlternateEmail}
                  onChange={(e) => setNewAlternateEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAlternateEmail())}
                  placeholder="Add alternate email..."
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={addAlternateEmail}
                  className="add-email-button"
                  disabled={loading}
                  title="Add email"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="instagram_handle">Instagram Handle</label>
            <input
              type="text"
              id="instagram_handle"
              name="instagram_handle"
              value={formData.instagram_handle}
              onChange={handleChange}
              disabled={loading}
              placeholder="@username or username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="spot_rate">Spot Rate</label>
            <select
              id="spot_rate"
              name="spot_rate"
              value={formData.spot_rate ?? 'standard'}
              onChange={handleSpotRateChange}
              disabled={loading}
            >
              <option value="standard">Standard ($25)</option>
              <option value="senior">Senior ($40)</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {formData.spot_rate === 'custom' && (
            <div className="form-group">
              <label htmlFor="custom_spot_rate">Custom Rate ($)</label>
              <input
                type="number"
                id="custom_spot_rate"
                name="custom_spot_rate"
                value={formData.custom_spot_rate ?? ''}
                onChange={handleCustomRateChange}
                disabled={loading}
                min={1}
                placeholder="e.g. 60"
              />
            </div>
          )}

          <div className="form-checkboxes">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="friend"
                name="friend"
                checked={formData.friend}
                onChange={handleChange}
                disabled={loading}
              />
              <label htmlFor="friend">Friend</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="is_cellar_comic"
                name="is_cellar_comic"
                checked={formData.is_cellar_comic}
                onChange={handleChange}
                disabled={loading}
              />
              <label htmlFor="is_cellar_comic">Cellar Comic</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="can_host"
                name="can_host"
                checked={formData.can_host}
                onChange={handleChange}
                disabled={loading}
              />
              <label htmlFor="can_host">Can Host</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="do_not_book"
                name="do_not_book"
                checked={formData.do_not_book}
                onChange={handleChange}
                disabled={loading}
              />
              <label htmlFor="do_not_book">Do Not Book</label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : comic ? 'Update Comic' : 'Create Comic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComicForm;
